import { router } from "@inertiajs/react"
import { useEffect, useRef } from "react"

import { subscribe, type AccountEvent, type ChannelEvents, type ChannelName, type UserEvent } from "@/lib/realtime"

let visitsInProgress = 0
let tracking = false

function trackVisits() {
  if (tracking) return
  tracking = true
  router.on("start", () => {
    visitsInProgress += 1
  })
  router.on("finish", () => {
    visitsInProgress = Math.max(0, visitsInProgress - 1)
  })
}

/**
 * Reloads the given page props when a matching event arrives on the channel.
 *
 * Events that arrive together cause one reload. The reload waits for a visit in flight: that
 * visit usually caused the event, and a concurrent request would take its flash message.
 */
function useChannelReload<Channel extends ChannelName>(
  channel: Channel,
  matches: (event: ChannelEvents[Channel]) => boolean,
  only: string[],
) {
  const latest = useRef({ matches, only })
  latest.current = { matches, only }

  useEffect(() => {
    trackVisits()
    let timeout: ReturnType<typeof setTimeout> | undefined

    const reloadWhenIdle = () => {
      if (visitsInProgress > 0) {
        timeout = setTimeout(reloadWhenIdle, 150)
        return
      }
      router.reload({ only: latest.current.only })
    }

    const unsubscribe = subscribe(channel, (event) => {
      if (!latest.current.matches(event)) return
      clearTimeout(timeout)
      timeout = setTimeout(reloadWhenIdle, 150)
    })

    return () => {
      clearTimeout(timeout)
      unsubscribe()
    }
  }, [channel])
}

/** Reloads the given page props when a matching event for the account arrives. */
export function useRealtimeReload(matches: (event: AccountEvent) => boolean, only: string[]) {
  useChannelReload("AccountChannel", matches, only)
}

/** Reloads the given page props when a matching event for the signed-in user arrives. */
export function useUserEventReload(matches: (event: UserEvent) => boolean, only: string[]) {
  useChannelReload("UserChannel", matches, only)
}
